/**
 * DataTable — sortable data table with optional row truncation and filter pills.
 */

import { useState, useMemo } from 'react'
import type { DataTableBlock } from '../../../stores/CoPilotStore'

export function DataTable({ block }: { block: DataTableBlock }) {
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [showAll, setShowAll] = useState(false)
  const [activeFilter, setActiveFilter] = useState('All')

  const maxRows = block.maxRows || 50

  // Derive filter values from rows when filterColumn is set
  const filterValues = useMemo(() => {
    if (!block.filterColumn) return []
    const col = block.filterColumn
    const unique = [...new Set(block.rows.map((r) => String(r[col] ?? '')))]
    return ['All', ...unique.sort()]
  }, [block.filterColumn, block.rows])

  // Filter rows
  let rows = [...block.rows]
  if (block.filterColumn && activeFilter !== 'All') {
    rows = rows.filter((r) => String(r[block.filterColumn!] ?? '') === activeFilter)
  }

  // Sort rows
  if (sortKey && block.sortable) {
    rows.sort((a, b) => {
      const av = a[sortKey] ?? ''
      const bv = b[sortKey] ?? ''
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? cmp : -cmp
    })
  }

  const truncated = !showAll && rows.length > maxRows
  if (truncated) {
    rows = rows.slice(0, maxRows)
  }

  // Hide the filterColumn from the visible table columns (it's shown as pills)
  const visibleColumns = block.filterColumn
    ? block.columns.filter((c) => c.key !== block.filterColumn)
    : block.columns

  function handleSort(key: string) {
    if (!block.sortable) return
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  // Count per filter value for badges
  const filterCounts = useMemo(() => {
    if (!block.filterColumn) return new Map<string, number>()
    const col = block.filterColumn
    const counts = new Map<string, number>()
    counts.set('All', block.rows.length)
    for (const row of block.rows) {
      const val = String(row[col] ?? '')
      counts.set(val, (counts.get(val) || 0) + 1)
    }
    return counts
  }, [block.filterColumn, block.rows])

  // ── Card layout ─────────────────────────────────────────────────────────
  if (block.layout === 'cards') {
    return (
      <div className="space-y-3">
        {block.title && (
          <div className="text-[14px] font-semibold text-[#18181B]">{block.title}</div>
        )}

        {/* Filter pills */}
        {filterValues.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {filterValues.map((val) => {
              const isActive = activeFilter === val
              const count = filterCounts.get(val) || 0
              return (
                <button
                  key={val}
                  onClick={() => setActiveFilter(val)}
                  className={`
                    inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-medium
                    transition-colors cursor-pointer
                    ${
                      isActive
                        ? 'bg-[#18181B] text-white'
                        : 'bg-[#F5F5F5] text-[#71717A] hover:bg-[#E4E4E7] hover:text-[#52525B]'
                    }
                  `}
                >
                  {val}
                  <span
                    className={`text-[11px] ${
                      isActive ? 'text-white/70' : 'text-[#A1A1AA]'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {/* Card grid */}
        {rows.length === 0 ? (
          <div className="text-center py-6 text-[13px] text-[#A1A1AA]">
            No listings match this filter
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            {rows.map((row, i) => {
              const headerCol = visibleColumns[0]
              const bodyCols = visibleColumns.slice(1)
              return (
                <div
                  key={i}
                  className="border border-[#E4E4E7] rounded-xl bg-white overflow-hidden"
                >
                  {/* Card header */}
                  <div className="px-3 py-2.5 border-b border-[#F4F4F5]">
                    <div className="text-[13px] font-semibold text-[#18181B] leading-snug line-clamp-2">
                      {row[headerCol.key] ?? '—'}
                    </div>
                  </div>
                  {/* Card body */}
                  <div className="px-3 py-2.5 space-y-1.5">
                    {bodyCols.map((col) => {
                      const val = String(row[col.key] ?? '—')
                      const isLong = val.length > 40
                      return (
                        <div
                          key={col.key}
                          className={
                            isLong
                              ? 'space-y-0.5'
                              : 'flex items-center justify-between'
                          }
                        >
                          <span className="text-[11px] text-[#A1A1AA] uppercase tracking-wide">
                            {col.label}
                          </span>
                          <span
                            className={`text-[12px] text-[#3F3F46] ${
                              isLong ? '' : 'font-medium text-right'
                            }`}
                          >
                            {val}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Show all */}
        {truncated && (
          <button
            onClick={() => setShowAll(true)}
            className="text-[13px] text-[#2563EB] hover:text-[#1D4ED8] font-medium cursor-pointer"
          >
            Show all {block.rows.length} items
          </button>
        )}
      </div>
    )
  }

  // ── Table layout (default) ──────────────────────────────────────────────
  return (
    <div className="border border-[#E5E5E5] rounded-xl overflow-hidden">
      {block.title && (
        <div className="px-4 py-2.5 bg-[#FAFAFA] border-b border-[#E5E5E5]">
          <span className="text-[14px] font-semibold text-[#18181B]">{block.title}</span>
        </div>
      )}

      {/* Filter pills */}
      {filterValues.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 py-2.5 border-b border-[#E5E5E5] bg-white">
          {filterValues.map((val) => {
            const isActive = activeFilter === val
            const count = filterCounts.get(val) || 0
            return (
              <button
                key={val}
                onClick={() => setActiveFilter(val)}
                className={`
                  inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-medium
                  transition-colors cursor-pointer
                  ${
                    isActive
                      ? 'bg-[#18181B] text-white'
                      : 'bg-[#F5F5F5] text-[#71717A] hover:bg-[#E4E4E7] hover:text-[#52525B]'
                  }
                `}
              >
                {val}
                <span
                  className={`text-[11px] ${
                    isActive ? 'text-white/70' : 'text-[#A1A1AA]'
                  }`}
                >
                  {count}
                </span>
              </button>
            )
          })}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-[14px]">
          <thead>
            <tr className="border-b border-[#E5E5E5]">
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-2 font-medium text-[#71717A] text-[12px] uppercase tracking-wide ${
                    col.align === 'right'
                      ? 'text-right'
                      : col.align === 'center'
                        ? 'text-center'
                        : 'text-left'
                  } ${block.sortable ? 'cursor-pointer hover:text-[#18181B] select-none' : ''}`}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  {sortKey === col.key && (
                    <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  className="px-4 py-6 text-center text-[13px] text-[#A1A1AA]"
                >
                  No listings match this filter
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr
                  key={i}
                  className="border-b border-[#F5F5F5] last:border-b-0 hover:bg-[#FAFAFA]"
                >
                  {visibleColumns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 py-2 text-[#3F3F46] ${
                        col.align === 'right'
                          ? 'text-right'
                          : col.align === 'center'
                            ? 'text-center'
                            : 'text-left'
                      }`}
                    >
                      {row[col.key] ?? '—'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {truncated && (
        <div className="px-4 py-2 border-t border-[#E5E5E5] bg-[#FAFAFA]">
          <button
            onClick={() => setShowAll(true)}
            className="text-[13px] text-[#2563EB] hover:text-[#1D4ED8] font-medium cursor-pointer"
          >
            Show all {block.rows.length} rows
          </button>
        </div>
      )}
    </div>
  )
}
